/**
 * PaymentProofViewer — uniform UI for inline preview + open + download of a
 * booking's payment slip / proof. Handles base64 data URIs and remote URLs.
 *
 * Props:
 *   raw       — the raw payment_proof string (data URI or /uploads/... URL)
 *   bookingId — used to derive the download filename
 *   compact   — if true, renders just a "View payment proof" button (no inline preview)
 */
import React, { useState } from 'react';
import { Button } from './ui/button';
import { Download, ExternalLink, FileText, Receipt } from 'lucide-react';
import { resolveProof } from '../lib/paymentProof';

export default function PaymentProofViewer({ raw, bookingId, compact = false, label = 'Payment Proof' }) {
  const [showFull, setShowFull] = useState(false);
  const proof = resolveProof(raw, bookingId);
  if (proof.kind === 'none' || !proof.src) return null;

  const download = async () => {
    try {
      if (proof.src.startsWith('data:')) {
        const a = document.createElement('a');
        a.href = proof.src; a.download = proof.filename;
        document.body.appendChild(a); a.click(); a.remove();
      } else {
        const res = await fetch(proof.src);
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = proof.filename;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
      }
    } catch (_) {
      window.open(proof.src, '_blank');
    }
  };

  const open = () => window.open(proof.src, '_blank', 'noopener,noreferrer');

  if (compact) {
    return (
      <div className="flex flex-wrap items-center gap-2" data-testid="payment-proof-viewer-compact">
        <Receipt className="w-4 h-4 text-gray-400" />
        <span className="text-sm text-gray-600">{label}</span>
        <Button size="sm" variant="outline" onClick={open} data-testid="payment-proof-open">
          <ExternalLink className="w-3 h-3 mr-1" /> Open
        </Button>
        <Button size="sm" variant="outline" onClick={download} data-testid="payment-proof-download">
          <Download className="w-3 h-3 mr-1" /> Download
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="payment-proof-viewer">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h4 className="font-medium text-sm flex items-center gap-1"><Receipt className="w-4 h-4 text-gray-400" /> {label}</h4>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={open} data-testid="payment-proof-open">
            <ExternalLink className="w-3 h-3 mr-1" /> Open
          </Button>
          <Button size="sm" variant="outline" onClick={download} data-testid="payment-proof-download">
            <Download className="w-3 h-3 mr-1" /> Download
          </Button>
        </div>
      </div>
      {/* Inline preview */}
      {proof.kind === 'image' && (
        <img
          src={proof.src}
          alt={label}
          className="max-w-full max-h-96 rounded border border-gray-200 cursor-zoom-in"
          onClick={() => setShowFull(true)}
        />
      )}
      {proof.kind === 'pdf' && (
        <iframe src={proof.src} title={label} className="w-full h-96 rounded border border-gray-200" />
      )}
      {(proof.kind === 'url' || proof.kind === 'unknown' || proof.kind === 'other') && proof.kind !== 'image' && proof.kind !== 'pdf' && (
        <div className="border border-dashed rounded p-3 text-sm text-gray-500 flex items-center gap-2">
          <FileText className="w-4 h-4" /> Preview not available — use Open or Download.
        </div>
      )}

      {/* Full-screen modal for images */}
      {showFull && proof.kind === 'image' && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 cursor-zoom-out"
          onClick={() => setShowFull(false)}
        >
          <img src={proof.src} alt={label} className="max-w-full max-h-full" />
        </div>
      )}
    </div>
  );
}
